import type { Track } from '../../api.js';
import { attentionStateFor, type AttentionState } from './statusMap.js';

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function AttentionStripRow({
  track,
  attention,
  onOpen,
}: {
  track: Track;
  attention: AttentionState;
  onOpen: (id: string) => void;
}) {
  return (
    <div className="attention-strip-row" data-kind={attention.kind}>
      <span className="attention-strip-kind-badge" data-kind={attention.kind}>
        {attention.kind === 'halted' ? 'HALTED — needs you' : 'NEEDS INPUT'}
      </span>
      <button type="button" className="attention-strip-title" onClick={() => onOpen(track.id)}>
        {track.title}
      </button>
      {attention.reason && <span className="attention-strip-reason">{attention.reason}</span>}
      {attention.since && <span className="attention-strip-age">{relativeTime(attention.since)}</span>}
      {attention.sessionUrl ? (
        <a
          className="attention-strip-open"
          href={attention.sessionUrl}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          Open session
        </a>
      ) : (
        <span
          className="attention-strip-open attention-strip-open-disabled"
          title="No session URL recorded for this track yet"
        >
          Open session
        </span>
      )}
    </div>
  );
}

// Top-of-board strip surfacing every track whose last Claude Code session
// halted or is waiting on operator input — kept separate from ActionStrip
// (state-machine-driven actions) since a track can need attention here in
// any state, including mid-build.
export default function AttentionStrip({ tracks, onOpen }: { tracks: Track[]; onOpen: (id: string) => void }) {
  const items = tracks
    .map((track) => ({ track, attention: attentionStateFor(track) }))
    .filter((x): x is { track: Track; attention: AttentionState } => x.attention !== null)
    .sort((a, b) => new Date(b.track.updated_at).getTime() - new Date(a.track.updated_at).getTime());

  if (items.length === 0) return null;

  return (
    <div className="attention-strip">
      <div className="attention-strip-header">
        <span className="attention-strip-header-title">🛑 Needs you</span>
        <span className="attention-strip-badge">{items.length}</span>
      </div>
      <div className="attention-strip-rows">
        {items.map(({ track, attention }) => (
          <AttentionStripRow key={track.id} track={track} attention={attention} onOpen={onOpen} />
        ))}
      </div>
    </div>
  );
}
